import { useMemo } from 'react';
import { useTableSort } from '../../hooks/useTableSort';
import { SortableTh } from '../payment-recon/SortableTh';
import { formatMoney, agingClass, AGING_BUCKETS } from '../../utils/arDunningFormat';
import type { ArOpenItem, AgingBucket } from '../../types/arDunning';

interface Props {
    items: ArOpenItem[];
    loading: boolean;
    onDrilldown: (customerName: string, bucket: string | null) => void;
}

/** Matrix columns: all buckets except the "no due date" one, which folds into Current. */
const COLS = AGING_BUCKETS.filter(b => b.key !== 'unknown');

type ColKey = AgingBucket;

interface Row {
    id: string;
    name: string;
    buckets: Record<string, number>;
    total: number;
    riba: number;   // issued RiBa (SBF) — shown apart, never in buckets/total
}

function emptyBuckets(): Record<string, number> {
    const o: Record<string, number> = {};
    for (const c of COLS) o[c.key] = 0;
    return o;
}

export function ArAgingTab({ items, loading, onDrilldown }: Props) {
    // The RiBa column is dit-only — show it only when the loaded set actually has issued RiBa.
    const showRiba = useMemo(() => items.some(i => i.ItemCategory === 'RibaIssued'), [items]);

    const rows = useMemo<Row[]>(() => {
        const map = new Map<string, Row>();
        for (const i of items) {
            const key = i.CustomerID;
            let row = map.get(key);
            if (!row) { row = { id: key, name: i.CustomerName ?? i.CustomerCode ?? key, buckets: emptyBuckets(), total: 0, riba: 0 }; map.set(key, row); }
            const amt = i.OpenAmountEUR;
            // Issued RiBa is collected by the bank — keep it out of the age buckets and the row total.
            if (i.ItemCategory === 'RibaIssued') { row.riba += amt; continue; }
            const col: ColKey = i.AgingBucket === 'unknown' ? 'current' : i.AgingBucket;
            row.buckets[col] = (row.buckets[col] ?? 0) + amt;
            row.total += amt;
        }
        return [...map.values()];
    }, [items]);

    const totals = useMemo(() => {
        const t = { buckets: emptyBuckets(), total: 0, riba: 0 };
        for (const r of rows) { for (const c of COLS) t.buckets[c.key] += r.buckets[c.key] ?? 0; t.total += r.total; t.riba += r.riba; }
        return t;
    }, [rows]);

    const accessors: Record<string, (r: Row) => number | string> = { customer: r => r.name, total: r => r.total, riba: r => r.riba };
    for (const c of COLS) accessors[c.key] = (r: Row) => r.buckets[c.key] ?? 0;
    const { sorted, sort, toggleSort } = useTableSort(rows, accessors, { key: 'total', dir: 'desc' });

    return (
        <div>
            <p className="mb-3 text-xs text-slate-500">Open receivables by customer and age (EUR-consolidated). Click a figure to open the matching items.</p>
            {loading ? (
                <div className="py-10 text-center text-sm text-slate-400">Loading…</div>
            ) : rows.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-200 py-10 text-center text-sm text-slate-400">No open receivables.</div>
            ) : (
                <div className="max-h-[65vh] overflow-auto rounded-lg border border-slate-200">
                    <table className="min-w-full divide-y divide-slate-200 text-sm">
                        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 [&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-slate-50">
                            <tr>
                                <SortableTh label="Customer" sortKey="customer" sort={sort} onSort={toggleSort} />
                                {COLS.map(c => <SortableTh key={c.key} label={c.label} sortKey={c.key} sort={sort} onSort={toggleSort} className="text-right" />)}
                                <SortableTh label="Total" sortKey="total" sort={sort} onSort={toggleSort} className="text-right" />
                                {showRiba && <SortableTh label="RiBa (SBF)" sortKey="riba" sort={sort} onSort={toggleSort} className="text-right" />}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {sorted.map(r => (
                                <tr key={r.id} className="hover:bg-slate-50">
                                    <td className="max-w-xs truncate px-3 py-2 text-slate-700" title={r.name}>
                                        <button onClick={() => onDrilldown(r.name, null)} className="truncate text-left hover:text-indigo-700 hover:underline">{r.name}</button>
                                    </td>
                                    {COLS.map(c => {
                                        const v = r.buckets[c.key] ?? 0;
                                        return (
                                            <td key={c.key} className="whitespace-nowrap px-3 py-2 text-right">
                                                {Math.abs(v) > 0.005
                                                    ? <button onClick={() => onDrilldown(r.name, c.key)} className={`hover:underline ${v < 0 ? 'text-emerald-700' : agingClass(c.key)}`}>{formatMoney(v)}</button>
                                                    : <span className="text-slate-300">–</span>}
                                            </td>
                                        );
                                    })}
                                    <td className={`whitespace-nowrap px-3 py-2 text-right font-medium ${r.total < 0 ? 'text-emerald-700' : 'text-slate-800'}`}>
                                        <button onClick={() => onDrilldown(r.name, null)} className="hover:underline">{formatMoney(r.total)}</button>
                                    </td>
                                    {showRiba && (
                                        <td className="whitespace-nowrap px-3 py-2 text-right text-blue-700">
                                            {Math.abs(r.riba) > 0.005
                                                ? <button onClick={() => onDrilldown(r.name, null)} className="hover:underline" title="Issued RiBa (SBF) — collected by the bank">{formatMoney(r.riba)}</button>
                                                : <span className="text-slate-300">–</span>}
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                        <tfoot className="border-t-2 border-slate-200 text-sm font-semibold text-slate-800 [&_td]:sticky [&_td]:bottom-0 [&_td]:bg-slate-50">
                            <tr>
                                <td className="px-3 py-2">Total ({rows.length})</td>
                                {COLS.map(c => <td key={c.key} className="whitespace-nowrap px-3 py-2 text-right">{formatMoney(totals.buckets[c.key])}</td>)}
                                <td className="whitespace-nowrap px-3 py-2 text-right">{formatMoney(totals.total)}</td>
                                {showRiba && <td className="whitespace-nowrap px-3 py-2 text-right text-blue-700">{formatMoney(totals.riba)}</td>}
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}
        </div>
    );
}
